/**
 * executeHomeAssistantAction
 *
 * Resolves a Home Assistant placeholder action attached to a component
 * (entityId, domain, service, serviceData with `{{value}}` placeholders)
 * into a live service call through the cloud relay.
 *
 * Refuses to fire when the binding's risk level is `review_required` —
 * those (locks, alarms, garage doors, HVAC extremes, irrigation, water,
 * MQTT/HTTP/SSH transports, etc.) need a deliberate human confirmation
 * UX that does not exist yet. They cannot be executed silently.
 */

import type { Component } from "@/types";
import { useHAStore } from "@/stores/integrations/ha-store";
import {
  extractHomeAssistantBinding,
  isHomeAssistantReviewRequired,
  type HomeAssistantBindingRiskLevel,
} from "./binding";

// The executor only reads `properties`; accept anything structurally
// compatible so test fixtures don't need to materialize a full Component.
type ComponentWithProperties = Pick<Component, "properties">;

interface PlaceholderAction {
  type: "home-assistant-placeholder";
  entityId: string;
  domain: string;
  service: string;
  serviceData?: Record<string, string | number | boolean>;
  riskLevel: HomeAssistantBindingRiskLevel;
}

interface ControlEvent {
  /** The raw value the user just produced — toggle bool, slider %, dial deg, etc. */
  value: unknown;
  /** Optional payload from the control component if it carries richer data. */
  parameters?: Record<string, unknown>;
}

export class HomeAssistantReviewRequiredError extends Error {
  public readonly entityId: string;
  public readonly domain: string;
  constructor(entityId: string, domain: string) {
    super(
      `Home Assistant action on ${entityId} (${domain}) is review-required and cannot run without explicit confirmation`,
    );
    this.name = "HomeAssistantReviewRequiredError";
    this.entityId = entityId;
    this.domain = domain;
  }
}

const isPlaceholderAction = (value: unknown): value is PlaceholderAction => {
  if (typeof value !== "object" || value === null) return false;
  if (!("type" in value) || value.type !== "home-assistant-placeholder") return false;
  if (!("entityId" in value) || typeof value.entityId !== "string") return false;
  if (!("domain" in value) || typeof value.domain !== "string") return false;
  if (!("service" in value) || typeof value.service !== "string") return false;
  if (
    !("riskLevel" in value) ||
    (value.riskLevel !== "low" && value.riskLevel !== "review_required")
  ) {
    return false;
  }
  return true;
};

/** Return the placeholder action if present, otherwise null. */
export function getHomeAssistantPlaceholderAction(
  component: ComponentWithProperties | null | undefined,
): PlaceholderAction | null {
  if (!component) return null;
  return isPlaceholderAction(component.properties.action) ? component.properties.action : null;
}

/**
 * Replace `{{value}}` template strings in serviceData with the actual value
 * from the control event. Returns the substituted record, or undefined if
 * the action had no serviceData.
 */
function resolveServiceData(
  action: PlaceholderAction,
  event: ControlEvent,
): Record<string, unknown> | undefined {
  const template = action.serviceData;
  if (!template) return undefined;

  const resolved: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(template)) {
    if (typeof raw === "string" && raw.includes("{{value}}")) {
      resolved[key] = event.value;
    } else {
      resolved[key] = raw;
    }
  }
  return resolved;
}

/**
 * For toggle-style services, refine into turn_on/turn_off when the control
 * event carried a definitive boolean intent. HA's `toggle` would also work
 * but explicit on/off is clearer in logs and lets the bridge short-circuit
 * if the device is already in the desired state.
 */
function refineService(action: PlaceholderAction, event: ControlEvent): string {
  if (action.service !== "toggle") return action.service;
  const v = event.value;
  if (typeof v === "boolean") return v ? "turn_on" : "turn_off";
  return action.service;
}

/**
 * Execute the placeholder action against the cloud relay.
 * Throws HomeAssistantReviewRequiredError if the binding is gated.
 */
export async function executeHomeAssistantAction(
  component: ComponentWithProperties,
  event: ControlEvent,
): Promise<void> {
  const action = getHomeAssistantPlaceholderAction(component);
  if (!action) {
    throw new Error("Component has no Home Assistant placeholder action");
  }

  if (
    action.riskLevel === "review_required" &&
    isHomeAssistantReviewRequired(action.entityId, action.domain)
  ) {
    throw new HomeAssistantReviewRequiredError(action.entityId, action.domain);
  }

  const binding = extractHomeAssistantBinding(component.properties);
  const instanceId = binding?.instanceId;
  if (!instanceId) {
    throw new Error(
      `Cannot execute ${action.entityId}: binding is missing instanceId. Re-bind the component to a current Home Assistant instance.`,
    );
  }

  const service = refineService(action, event);
  const serviceData = resolveServiceData(action, event);

  await useHAStore.getState().callService(
    instanceId,
    action.domain,
    service,
    action.entityId,
    serviceData,
  );
}
